import type { ApiClientCore } from './apiClient.core.js';
import type { ApiResponse } from './apiClient.types.js';

export const rbacMethods = {
  /**
   * Get all users (SUPER_DEV only)
   */
  async getUsers(this: ApiClientCore): Promise<ApiResponse<{
    users: Array<{
      id: number;
      email: string;
      name: string | null;
      role: string;
      userType: string;
      status: string;
      tenantId: number | null;
      tenantAccess: number[];
      capabilities: string[];
    }>;
  }>> {
    return this.get('/api/v1/admin/users');
  },

  /**
   * Get user by ID (SUPER_DEV only)
   */
  async getUser(this: ApiClientCore, id: number): Promise<ApiResponse<{
    user: {
      id: number;
      email: string;
      name: string | null;
      role: string;
      userType: string;
      status: string;
      tenantId: number | null;
      tenantAccess: number[];
      capabilities: string[];
    };
  }>> {
    return this.get(`/api/v1/admin/users/${id}`);
  },

  /**
   * Get user tenant access (SUPER_DEV only)
   */
  async getUserTenants(this: ApiClientCore, id: number): Promise<ApiResponse<{
    tenantAccess: Array<{
      id: number;
      adminId: number;
      tenantId: number;
      createdAt: string;
      updatedAt: string;
    }>;
  }>> {
    return this.get(`/api/v1/admin/users/${id}/tenants`);
  },

  /**
   * Assign tenant to user (SUPER_DEV only)
   */
  async assignTenant(this: ApiClientCore, id: number, tenantId: number): Promise<ApiResponse<{
    message: string;
  }>> {
    return this.post(`/api/v1/admin/users/${id}/tenants`, { tenantId });
  },

  /**
   * Remove tenant access from user (SUPER_DEV only)
   */
  async removeTenant(this: ApiClientCore, id: number, tenantId: number): Promise<ApiResponse<{
    message: string;
  }>> {
    return this.delete(`/api/v1/admin/users/${id}/tenants/${tenantId}`);
  },

  async deactivateUser(this: ApiClientCore, id: number): Promise<ApiResponse<{ deactivated: boolean }>> {
    return this.post(`/api/v1/admin/users/${id}/deactivate`, {});
  },

  async reactivateUser(this: ApiClientCore, id: number): Promise<ApiResponse<{ reactivated: boolean }>> {
    return this.post(`/api/v1/admin/users/${id}/reactivate`, {});
  },

  async permanentlyDeleteUser(this: ApiClientCore, id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.delete(`/api/v1/admin/users/${id}/permanent-delete`);
  },

  /**
   * Get user capabilities (SUPER_DEV only)
   */
  async getUserCapabilities(this: ApiClientCore, id: number, tenantId?: number | null): Promise<ApiResponse<{
    capabilities: Array<{
      id: number;
      capability: string;
      tenantId: number | null;
      grantedBy: number;
      grantedAt: string;
      revokedAt: string | null;
      isActive: boolean;
    }>;
  }>> {
    const query = typeof tenantId === 'number' ? `?tenantId=${tenantId}` : '';
    return this.get(`/api/v1/admin/users/${id}/capabilities${query}`);
  },

  /**
   * Grant capability to user (SUPER_DEV or SUPER_ADMIN)
   */
  async grantCapability(
    this: ApiClientCore,
    id: number,
    capability: string,
    tenantId?: number | null
  ): Promise<ApiResponse<{
    capability: {
      id: number;
      capability: string;
      tenantId: number | null;
      grantedBy: number;
      grantedAt: string;
      isActive: boolean;
    };
  }>> {
    return this.post(`/api/v1/admin/users/${id}/capabilities`, {
      capability,
      tenantId: typeof tenantId === 'number' ? tenantId : null,
    });
  },

  /**
   * Revoke capability from user (SUPER_DEV or SUPER_ADMIN)
   */
  async revokeCapability(
    this: ApiClientCore,
    id: number,
    capability: string,
    tenantId?: number | null
  ): Promise<ApiResponse<{
    message: string;
  }>> {
    const query = typeof tenantId === 'number' ? `?tenantId=${tenantId}` : '';
    return this.delete(`/api/v1/admin/users/${id}/capabilities/${encodeURIComponent(capability)}${query}`);
  },

  /**
   * Get all available capabilities (SUPER_ADMIN / AdminCapability).
   * AdminCapability always passes `includeNotEntitled: true` so Q9 can show disabled rows.
   * Pass `tenantId` / `scope: 'all'` so commercial annotate uses resolveListTenantId.
   */
  async getAvailableCapabilities(
    this: ApiClientCore,
    options?: {
      readonly includeNotEntitled?: boolean;
      readonly tenantId?: number;
      readonly scope?: 'all';
    },
  ): Promise<ApiResponse<{
    capabilities: Array<{
      name: string;
      description: string;
      sensitivity: string;
      includes: readonly string[];
      excludes: readonly string[];
      canonicalId?: string;
      displayKey?: string;
      domain?: string;
      level?: 'view' | 'manage';
      isException?: boolean;
      exceptionClass?: string | null;
      entitled?: boolean;
      requiredBlockKeys?: readonly string[];
    }>;
  }>> {
    const params = new URLSearchParams();
    if (options?.includeNotEntitled === true) {
      params.set('includeNotEntitled', 'true');
    }
    if (options?.tenantId !== undefined && options.tenantId > 0) {
      params.set('tenantId', String(options.tenantId));
    } else if (options?.scope === 'all') {
      params.set('scope', 'all');
    }
    const query = params.toString();
    return this.get(`/api/v1/admin/capabilities${query.length > 0 ? `?${query}` : ''}`);
  },

  async getCapabilitiesSummary(
    this: ApiClientCore,
    options?: { readonly tenantId?: number; readonly scope?: 'all' },
  ): Promise<ApiResponse<{
    roleTemplateCount: number;
    systemRoleCount: number;
    userCount: number;
    permissionCatalogCount: number;
    assignmentCount: number;
    loginsLast24h: number;
    failedLoginsLast24h: number | null;
    generatedAt: string;
  } | null>> {
    let query = '';
    if (options?.tenantId !== undefined && options.tenantId > 0) {
      query = `?tenantId=${options.tenantId}`;
    } else if (options?.scope === 'all') {
      query = '?scope=all';
    }
    return this.get(`/api/v1/admin/capabilities/summary${query}`);
  },

  async getRoleTemplates(
    this: ApiClientCore,
    options?: { readonly tenantId?: number; readonly scope?: 'all' },
  ): Promise<ApiResponse<{
    templates: Array<{
      templateId: string;
      displayKey: string;
      description: string;
      capabilityCount: number;
      capabilities?: string[];
    }>;
  }>> {
    let query = '';
    if (options?.tenantId !== undefined && options.tenantId > 0) {
      query = `?tenantId=${options.tenantId}`;
    } else if (options?.scope === 'all') {
      query = '?scope=all';
    }
    return this.get(`/api/v1/admin/role-templates${query}`);
  },

  async applyRoleTemplateToAdmin(
    this: ApiClientCore,
    id: number,
    templateId: string,
    tenantId?: number | null,
  ): Promise<ApiResponse<{
    grantedCount: number;
    capabilities: Array<{ id: number; capability: string; tenantId: number | null }>;
    skippedCapabilities: Array<{ key: string; missingBlockKeys: string[] }>;
  }>> {
    return this.post(`/api/v1/admin/capabilities/users/${id}/apply-template`, {
      templateId,
      tenantId: tenantId !== undefined && tenantId !== null ? tenantId : null,
    });
  },

  /**
   * Get all available capabilities (SUPER_DEV, dev dashboard)
   */
  async getAvailableCapabilitiesDev(this: ApiClientCore): Promise<ApiResponse<{
    capabilities: Array<{
      name: string;
      description: string;
      sensitivity: string;
      includes: readonly string[];
      excludes: readonly string[];
      canonicalId?: string;
      displayKey?: string;
      domain?: string;
      level?: 'view' | 'manage';
      isException?: boolean;
      exceptionClass?: string | null;
    }>;
  }>> {
    return this.get('/api/v1/dev/capabilities', true);
  },

  /**
   * Get ADMIN user capabilities (SUPER_ADMIN only)
   */
  async getAdminUserCapabilities(
    this: ApiClientCore,
    id: number,
    tenantId?: number | null
  ): Promise<ApiResponse<{
    capabilities: Array<{
      id: number;
      capability: string;
      grantedBy: number;
      grantedAt: string;
      revokedAt: string | null;
      isActive: boolean;
    }>;
  }>> {
    const query = typeof tenantId === 'number' ? `?tenantId=${tenantId}` : '';
    return this.get(`/api/v1/admin/capabilities/users/${id}${query}`);
  },

  /**
   * Grant capability to ADMIN user (SUPER_ADMIN only)
   * tenantId should always be provided (SUPER_ADMIN is tenant-scoped)
   * The backend enforces tenant scope validation via enforceTenantScope middleware.
   */
  async grantCapabilityToAdmin(
    this: ApiClientCore,
    id: number,
    capability: string,
    tenantId?: number | null,
    options?: {
      delegationApproved?: boolean;
      reason?: string;
      breakGlassToken?: string;
      approvalMode?: 'dualControl' | 'peer';
      typedPhrase?: string;
      impactAcknowledged?: boolean;
      dataHandlingNoticeAcknowledged?: boolean;
    },
  ): Promise<ApiResponse<{
    capability: {
      id: number;
      capability: string;
      tenantId: number | null;
      grantedBy: number;
      grantedAt: string;
      isActive: boolean;
    };
  }>> {
    return this.post(`/api/v1/admin/capabilities/users/${id}`, {
      capability,
      tenantId: tenantId !== undefined && tenantId !== null ? tenantId : null,
      ...options,
    });
  },

  /** SoD #2 — create pending exception-grant dual-control request (not claims). */
  async requestExceptionGrantDualControl(
    this: ApiClientCore,
    body: {
      targetUserId: number;
      capability: string;
      tenantId?: number | null;
      reason: string;
      typedPhrase?: string;
      impactAcknowledged?: boolean;
      dataHandlingNoticeAcknowledged?: boolean;
      breakGlassToken?: string;
    },
  ): Promise<
    ApiResponse<{
      approvalRequestId: string;
      status: string;
      capability: string;
      targetUserId: number;
      expiresAt: string;
    }>
  > {
    return this.post('/api/v1/admin/capabilities/exception-grant-approvals', body);
  },

  async listPendingExceptionGrantApprovals(
    this: ApiClientCore,
    tenantId?: number | null,
  ): Promise<
    ApiResponse<{
      pending: Array<{
        approvalRequestId: string;
        status: string;
        capability: string;
        targetUserId: number;
        requesterId: number;
        reason: string;
        expiresAt: string;
      }>;
    }>
  > {
    const query =
      typeof tenantId === 'number' ? `?tenantId=${tenantId}` : '';
    return this.get(`/api/v1/admin/capabilities/exception-grant-approvals/pending${query}`);
  },

  async approveExceptionGrantDualControl(
    this: ApiClientCore,
    approvalRequestId: string,
    reason: string,
  ): Promise<ApiResponse<{ approvalRequestId: string; status: string }>> {
    return this.post(
      `/api/v1/admin/capabilities/exception-grant-approvals/${approvalRequestId}/approve`,
      { reason },
    );
  },

  async rejectExceptionGrantDualControl(
    this: ApiClientCore,
    approvalRequestId: string,
    reason: string,
  ): Promise<ApiResponse<{ approvalRequestId: string; status: string }>> {
    return this.post(
      `/api/v1/admin/capabilities/exception-grant-approvals/${approvalRequestId}/reject`,
      { reason },
    );
  },

  async executeExceptionGrantDualControl(
    this: ApiClientCore,
    approvalRequestId: string,
  ): Promise<
    ApiResponse<{ approvalRequestId: string; capability: string; targetUserId: number }>
  > {
    return this.post(
      `/api/v1/admin/capabilities/exception-grant-approvals/${approvalRequestId}/execute`,
      {},
    );
  },

  /**
   * Revoke capability from ADMIN user (SUPER_ADMIN only)
   * tenantId should always be provided (SUPER_ADMIN is tenant-scoped)
   * The backend enforces tenant scope validation via enforceTenantScope middleware.
   */
  async revokeCapabilityFromAdmin(
    this: ApiClientCore,
    id: number,
    capability: string,
    tenantId?: number | null
  ): Promise<ApiResponse<{
    message: string;
  }>> {
    const query = tenantId !== undefined && tenantId !== null ? `?tenantId=${tenantId}` : '';
    return this.delete(`/api/v1/admin/capabilities/users/${id}/${encodeURIComponent(capability)}${query}`);
  },

  /**
   * Get ADMIN users in current tenant (SUPER_ADMIN / SUPER_DEV).
   * Pass `tenantId` to act for a tenant, or `scope: 'all'` for cross-tenant (SUPER_DEV).
   * On Dev (`/dev`) hosts, `withTenantInPath` does not inject a tenant segment — callers
   * must pass query options (`scope: 'all'` or `tenantId`) so the list is not empty.
   */
  async getAdminUsersInTenant(
    this: ApiClientCore,
    options?: { readonly tenantId?: number; readonly scope?: 'all' },
  ): Promise<ApiResponse<{
    users: Array<{
      id: number;
      email: string;
      username?: string | null;
      name: string | null;
      role: string;
      userType: string;
      status: string;
      tenantId: number | null;
      lastLoginAt?: string | null;
      updatedAt?: string | null;
    }>;
  }>> {
    let query = '';
    if (options?.tenantId !== undefined && options.tenantId > 0) {
      query = `?tenantId=${options.tenantId}`;
    } else if (options?.scope === 'all') {
      query = '?scope=all';
    }
    return this.get(`/api/v1/admin/capabilities/users${query}`);
  },

  async createDelegatedTenantAdminForDev(
    this: ApiClientCore,
    body: {
      tenantId: number;
      email: string;
      role: 'ADMIN';
      provisionMode: 'password' | 'oidc_invite';
      password?: string;
      name?: string;
      username?: string;
      /** Invite email language (cs|en|sk); Accept-Language used when omitted. */
      locale?: 'cs' | 'en' | 'sk';
      templateId?: string;
      salesPointIds?: number[];
    },
  ): Promise<ApiResponse<{
    user: {
      id: number;
      email: string;
      username: string | null;
      name: string | null;
      role: string;
      status: string;
      tenantId: number | null;
    };
  }>> {
    return this.post('/api/v1/dev/delegated-admins', body, true);
  },

  async deactivateTenantAdminUser(this: ApiClientCore, id: number): Promise<ApiResponse<{ deactivated: boolean }>> {
    return this.post(`/api/v1/admin/tenant-admins/${id}/deactivate`, {});
  },

  async reactivateTenantAdminUser(this: ApiClientCore, id: number): Promise<ApiResponse<{ reactivated: boolean }>> {
    return this.post(`/api/v1/admin/tenant-admins/${id}/reactivate`, {});
  },

  async permanentlyDeleteTenantAdminUser(this: ApiClientCore, id: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.delete(`/api/v1/admin/tenant-admins/${id}/permanent-delete`);
  },

  async transferTenantOwnerAssignment(
    this: ApiClientCore,
    tenantId: number,
    body: { fromAdminUserId: number; toAdminUserId: number },
  ): Promise<ApiResponse<{ transferred: boolean; scope: 'TENANT' }>> {
    return this.post(`/api/v1/admin/tenants/${tenantId}/owner-assignments/transfer`, body);
  },

  /** POST /api/v1/admin/tenant-admins/super-admin (CT-RBAC-345). CSRF Yes. */
  async createTenantSuperAdmin(
    this: ApiClientCore,
    body: {
      email: string;
      password: string;
      name?: string;
      username?: string;
    },
  ): Promise<ApiResponse<{
    user: {
      id: number;
      email: string;
      username: string | null;
      name: string | null;
      role: string;
      status: string;
      tenantId: number | null;
    };
  }>> {
    return this.post('/api/v1/admin/tenant-admins/super-admin', body, false);
  },

  /**
   * Get DEV users (SUPER_DEV only, dev dashboard)
   */
  async getDevUsers(this: ApiClientCore): Promise<ApiResponse<{
    users: Array<{
      id: number;
      email: string;
      name: string | null;
      role: string;
      userType: string;
      status: string;
      tenantId: number | null;
      tenantAccess: number[];
      capabilities: string[];
    }>;
  }>> {
    return this.get('/api/v1/dev/users', true);
  },

  /**
   * Get DEV user details (SUPER_DEV only, dev dashboard)
   */
  async getDevUser(this: ApiClientCore, id: number): Promise<ApiResponse<{
    user: {
      id: number;
      email: string;
      name: string | null;
      role: string;
      userType: string;
      status: string;
      tenantId: number | null;
      tenantAccess: number[];
      capabilities: string[];
    };
  }>> {
    return this.get(`/api/v1/dev/users/${id}`, true);
  },

  /**
   * Get DEV user capabilities (SUPER_DEV only, dev dashboard)
   * tenantId can be a number (tenant-specific) or null (global/dev dashboard capabilities)
   * When null, omit from query - backend will handle via default tenant resolution
   */
  async getDevUserCapabilities(this: ApiClientCore, id: number, tenantId?: number | null): Promise<ApiResponse<{
    capabilities: Array<{
      id: number;
      capability: string;
      tenantId: number | null;
      grantedBy: number;
      grantedAt: string;
      revokedAt: string | null;
      isActive: boolean;
    }>;
  }>> {
    const query = typeof tenantId === 'number' ? `?tenantId=${tenantId}` : '';
    return this.get(`/api/v1/dev/users/${id}/capabilities${query}`, true);
  },

  /**
   * Grant capability to DEV user (SUPER_DEV only, dev dashboard)
   * tenantId can be a number (tenant-specific) or null (global/dev dashboard capabilities)
   */
  async grantCapabilityToDev(
    this: ApiClientCore,
    id: number,
    capability: string,
    tenantId?: number | null
  ): Promise<ApiResponse<{
    capability: {
      id: number;
      capability: string;
      tenantId: number | null;
      grantedBy: number;
      grantedAt: string;
      isActive: boolean;
    };
  }>> {
    return this.post(
      `/api/v1/dev/users/${id}/capabilities`,
      {
        capability,
        tenantId: tenantId !== undefined ? tenantId : null,
      },
      true
    );
  },

  /**
   * Revoke capability from DEV user (SUPER_DEV only, dev dashboard)
   * tenantId can be a number (tenant-specific) or null (global/dev dashboard capabilities)
   * When null, omit from query - backend enforceTenantScope will use default tenant resolution
   */
  async revokeCapabilityFromDev(
    this: ApiClientCore,
    id: number,
    capability: string,
    tenantId?: number | null
  ): Promise<ApiResponse<{
    message: string;
  }>> {
    const query = typeof tenantId === 'number' ? `?tenantId=${tenantId}` : '';
    return this.delete(`/api/v1/dev/users/${id}/capabilities/${encodeURIComponent(capability)}${query}`, true);
  },

  /**
   * Assign tenant to DEV user (SUPER_DEV only, dev dashboard)
   */
  async assignTenantToDev(this: ApiClientCore, id: number, tenantId: number): Promise<ApiResponse<{
    message: string;
  }>> {
    return this.post(`/api/v1/dev/users/${id}/tenants`, { tenantId }, true);
  },

  /**
   * Remove tenant access from DEV user (SUPER_DEV only, dev dashboard)
   */
  async removeTenantFromDev(this: ApiClientCore, id: number, tenantId: number): Promise<ApiResponse<{
    message: string;
  }>> {
    return this.delete(`/api/v1/dev/users/${id}/tenants/${tenantId}`, true);
  },
};
